import { CaseType, TransformedCaseType, CaseFullType, TransformedCaseFullType } from './types';
import { API_CONFIG, getDefaultHeaders, API_ERROR_MESSAGES } from '../config/api';

/**
 * Transforms API case data to internal format
 * @param {CaseType} caseItem - Raw case data from API
 * @returns {TransformedCaseType} - Transformed case data
 */
const transformCaseData = (caseItem) => {
  return {
    id: caseItem.id.toString(),
    title: caseItem.title || 'Sin título',
    subtitle: caseItem.category?.name || 'Sin categoría',
    docomunt: caseItem.description || 'Sin descripción',
    slug: `case-${caseItem.id}`,
    pimg1: caseItem.featuredImageVertical || caseItem.featuredImage || '', // Vertical image for portfolio list
    pSimg: caseItem.featuredImage || '', // Horizontal image for single case view
    location: caseItem.time || 'N/A',
    date: caseItem.createdAt ? 
      new Date(caseItem.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : 'Fecha no disponible',
    isVisible: caseItem.isVisible !== false,
    // Store original API data for reference
    originalData: caseItem
  };
};

/**
 * Transforms full API case data to internal format
 * @param {CaseFullType} caseItem - Raw full case data from API
 * @returns {TransformedCaseFullType} - Transformed full case data
 */
const transformCaseFullData = (caseItem) => {
  console.log('transformCaseFullData - Input:', caseItem);
  console.log('transformCaseFullData - benefits:', caseItem.benefits);
  console.log('transformCaseFullData - postOps:', caseItem.postOps);
  
  const transformed = {
    id: caseItem.id.toString(),
    title: caseItem.title || 'Sin título',
    subtitle: caseItem.category?.name || 'Sin categoría',
    docomunt: caseItem.description || 'Sin descripción',
    slug: `case-${caseItem.id}`,
    pimg1: caseItem.featuredImageVertical || caseItem.featuredImage || '', // Vertical image for portfolio list
    pSimg: caseItem.featuredImage || '', // Horizontal image for single case view
    location: caseItem.time || 'N/A',
    date: caseItem.createdAt ? 
      new Date(caseItem.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : 'Fecha no disponible',
    isVisible: caseItem.isVisible !== false,
    benefits: caseItem.benefits || [],
    postOps: caseItem.postOps || [],
    // Store original API data for reference
    originalData: caseItem
  };
  
  console.log('transformCaseFullData - Output:', transformed);
  return transformed;
};

/**
 * Fetches cases from external API
 * @returns {Promise<TransformedCaseType[]>} - Array of transformed cases
 */
export const fetchCases = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CASES}?filter=isVisible%7C%7C%24eq%7C%7Ctrue`, {
      method: 'GET',
      headers: getDefaultHeaders(),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorMessage = API_ERROR_MESSAGES.UNKNOWN_ERROR;
      
      switch (response.status) {
        case 404:
          errorMessage = API_ERROR_MESSAGES.NOT_FOUND;
          break;
        case 401:
          errorMessage = API_ERROR_MESSAGES.UNAUTHORIZED;
          break;
        case 403:
          errorMessage = API_ERROR_MESSAGES.FORBIDDEN;
          break;
        case 500:
          errorMessage = API_ERROR_MESSAGES.SERVER_ERROR;
          break;
        default:
          errorMessage = `Error HTTP: ${response.status}`;
      }
      
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    
    // Validate that data is an array
    if (!Array.isArray(data)) {
      throw new Error('API response is not an array');
    }
    
    const transformedCases = data
      .filter(caseItem => caseItem && typeof caseItem === 'object')
      .map(transformCaseData);
    
    return transformedCases;
    
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(API_ERROR_MESSAGES.TIMEOUT_ERROR);
    }
    
    console.error('Error fetching cases:', error);
    throw error;
  }
};

/**
 * Fetches a single case by ID
 * @param {string|number} caseId - The case ID to fetch
 * @returns {Promise<TransformedCaseType|null>} - Single case or null if not found
 */
export const fetchCaseById = async (caseId) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CASE_BY_ID(caseId)}`, {
      method: 'GET',
      headers: getDefaultHeaders(),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null; // Case not found
      }
      
      let errorMessage = API_ERROR_MESSAGES.UNKNOWN_ERROR;
      
      switch (response.status) {
        case 401:
          errorMessage = API_ERROR_MESSAGES.UNAUTHORIZED;
          break;
        case 403:
          errorMessage = API_ERROR_MESSAGES.FORBIDDEN;
          break;
        case 500:
          errorMessage = API_ERROR_MESSAGES.SERVER_ERROR;
          break;
        default:
          errorMessage = `Error HTTP: ${response.status}`;
      }
      
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return transformCaseData(data);
    
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(API_ERROR_MESSAGES.TIMEOUT_ERROR);
    }
    
    console.error(`Error fetching case ${caseId}:`, error);
    throw error;
  }
};

/**
 * Fetches a full case with benefits and post-ops by ID
 * @param {string|number} caseId - The case ID to fetch
 * @returns {Promise<TransformedCaseFullType|null>} - Full case or null if not found
 */
export const fetchCaseFull = async (caseId) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CASE_FULL(caseId)}`, {
      method: 'GET',
      headers: getDefaultHeaders(),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Raw full case API response:', data);

    if (!data || typeof data !== 'object') {
      console.warn('API response is not an object:', data);
      return null;
    }

    const transformedCase = transformCaseFullData(data);
    console.log('Transformed full case:', transformedCase);
    return transformedCase;

  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      console.error('Request timeout:', error);
      throw new Error(API_ERROR_MESSAGES.TIMEOUT_ERROR);
    }

    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network error:', error);
      throw new Error(API_ERROR_MESSAGES.NETWORK_ERROR);
    }

    console.error('Error fetching full case:', error);
    throw error;
  }
};

/**
 * Fetches related cases for a specific case
 * @param {string|number} caseId - The case ID to get related cases for
 * @returns {Promise<TransformedCaseType[]>} - Array of related cases
 */
export const fetchRelatedCases = async (caseId) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CASE_RELATED(caseId)}`, {
      method: 'GET',
      headers: getDefaultHeaders(),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    
    if (!response.ok) {
      if (response.status === 404) {
        return []; // No related cases found
      }
      
      let errorMessage = API_ERROR_MESSAGES.UNKNOWN_ERROR;
      
      switch (response.status) {
        case 401:
          errorMessage = API_ERROR_MESSAGES.UNAUTHORIZED;
          break;
        case 403:
          errorMessage = API_ERROR_MESSAGES.FORBIDDEN;
          break;
        case 500:
          errorMessage = API_ERROR_MESSAGES.SERVER_ERROR;
          break;
        default:
          errorMessage = `Error HTTP: ${response.status}`;
      }
      
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    
    // Validate that data is an array
    if (!Array.isArray(data)) {
      console.warn('Related cases API response is not an array:', data);
      return [];
    }
    
    const transformedCases = data
      .filter(caseItem => caseItem && typeof caseItem === 'object')
      .map(transformCaseData);
    
    return transformedCases;
    
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(API_ERROR_MESSAGES.TIMEOUT_ERROR);
    }
    
    console.error(`Error fetching related cases for case ${caseId}:`, error);
    return []; // Return empty array on error
  }
};

// Fallback data in case API fails
const fallbackProjects = [
  {
    id: "1",
    title: "Rinoplastia funcional y estética",
    subtitle: "Cirugía Plástica",
    docomunt: "<p>Presentamos el caso de la paciente J.L.,<br> de 32 años, quien buscaba una mejora estética y funcional de su nariz, específicamente la reducción de una giba dorsal prominente y la afinación de la punta. Tras una evaluación exhaustiva y un análisis facial detallado, se recomendó una rinoplastia ultrasónica</p>.",
    slug: "portfolio-1",
    pimg1: require('../images/porfolio/1.png'),
    pSimg: require('../images/project-single/1.jpg'),
    location: "7 Lake Street,London",
    date: "15 Dec 2024",
    isVisible: true,
    originalData: null
  },
  {
    id: "2",
    title: "Rinoplastia funcional y estética ",
    subtitle: "Cirugía Plástica",
    docomunt: "Quis id at rhoncus consequat. Aliquam in. Velit phasellus augue tristique integer arcu. Elit sed vestibulum tristique suspendisse ut sit",
    slug: "Orthopaedics-Center",
    pimg1: require('../images/porfolio/2.png'),
    pSimg: require('../images/project-single/2.jpg'),
    location: "7 Lake Street,London",
    date: "15 Dec 2024",
    isVisible: true,
    originalData: null
  },
  {
    id: "3",
    title: "Rinoplastia funcional y estética ",
    subtitle: "Cirugía Plástica",
    docomunt: "Quis id at rhoncus consequat. Aliquam in. Velit phasellus augue tristique integer arcu. Elit sed vestibulum tristique suspendisse ut sit",
    slug: "Neurology-Services",
    pimg1: require('../images/porfolio/3.png'),
    pSimg: require('../images/project-single/3.jpg'),
    location: "7 Lake Street,London",
    date: "15 Dec 2024",
    isVisible: true,
    originalData: null
  },
];

export default fallbackProjects;






